import React, { useContext, useEffect, useState, useRef } from 'react';
import PageHeader from '../PageHeader/PageHeader';
import ProfileSearch from '../ProfileSearch/ProfileSearch';
import Tabs from '../gdk/Tabs/Tabs';
import AgencyDefaultsManagementTabContent from './AgencyDefaultsManagementTabContent';
import IvansRequestTabContent from './IvansRequestTabContent';
import ReportsTabContent from './ReportsTabContent';
import BankTabContent from './BankTabContent';
import { ClientConfigurationContext } from '../../context/ClientConfigurationContextProvider';
import { UserContext } from '../../context/UserContextProvider';
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout";
import AdminTabContent from './AdminTabContent';
import { AgentAccessContext } from '../../context/AgentAccessContextProvider';
import Error from '../Error';
import { getAgencyProfile, updateTaxInfoOnFile } from "../../api/agency-profile";
import { getBankInfo } from '../../api/billing';
import Toast, { ToastTypes } from "../gdk/Toast/Toast";
import PropTypes from 'prop-types';
import { getAgencyAlerts, updateAlert } from '../../api/alerts';
import Loader, { Types } from '../gdk/Loader';
import { recompileProfile } from '../../api/identity';

export default function Home({ tabName }) {
    const navigate = useNavigate();
    const clientConfiguration = useContext(ClientConfigurationContext);
    const user = useContext(UserContext);
    const agentAccess = useContext(AgentAccessContext);
    const [taxInfoOnFile, setTaxInfoOnFile] = useState();
    const [hasAcct, setHasAcct] = useState();
    const [acctNumber, setAcctNumber] = useState();
    const [acctType, setAcctType] = useState();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const confirmationToastRef = useRef(null);
    const infoToastRef = useRef(null);

    const show = {
        profileManagement: clientConfiguration.enableProfileManagement && user.authorizations?.canManageProfileManagement,
        agencyDefaults: clientConfiguration.enableAgencyDefaults && user.authorizations?.canSetAgencyDefaults,
        ivansRequest: clientConfiguration.enableIvansRequest && user.authorizations?.canManageIvansRequests,
        reports: clientConfiguration.enableReports && user.authorizations?.canAccessReports && agentAccess?.externalReportsEnabled,
        bankTab: clientConfiguration.enableBankTab && user.authorizations?.canManageBanking,
        adminTab: clientConfiguration.enableAdminTab && user.authorizations?.canManageAdmin,
    };

    const tabContent = {
        ...(show.profileManagement ? { "Profile Management": <ProfileSearch /> } : {}),
        ...(show.agencyDefaults ? { "Agency Defaults Management": <AgencyDefaultsManagementTabContent /> } : {}),
        ...(show.ivansRequest ? { "Ivans Request": <IvansRequestTabContent /> } : {}),
        ...(show.reports ? { "Reports": <ReportsTabContent /> } : {}),
        ...(show.bankTab ? {
            "Bank and W-9 Info": <BankTabContent
                w9={taxInfoOnFile}
                hasAcct={hasAcct}
                acctNumber={acctNumber}
                acctType={acctType} />
        } : {}),
        ...(show.adminTab ? { "Admin": <AdminTabContent /> } : {}),
    };

    const openTab = Object.keys(tabContent).findIndex(tab => tab === tabName) + 1 || 1;

    useEffect(() => {
        if (Object.entries(show).every(([key, value]) => (key === "reports" && !!value) || (key !== "reports" && !value))) {
            navigate("/reports");
        }
    });

    useEffect(() => {
        getAgencyProfile(user.agencyId, response => setTaxInfoOnFile(response.taxInfoOnFile));
    }, []);

    useEffect(() => {
        if (clientConfiguration.enableBankTab && user.authorizations?.canManageBanking) {
            getBankInfo(response => {
                setAcctNumber(response.bankAccountNumber);
                setAcctType(response.achBankAccountType);
                setHasAcct(response.hasBankAccount);
            }, () => { });
        }
    }, []);

    useEffect(() => {
        getAlerts();
    }, []);

    useEffect(() => {
        const redirectEvent = searchParams.get("event");
        const isHomeDocusign = searchParams.get("isHomeDocusign");
        const returnedState = searchParams.get("state");

        if (isHomeDocusign && redirectEvent && alerts.length > 0) {
            if (!returnedState) {
                infoToastRef.current.show();
                return;
            }
            if (redirectEvent === "signing_complete" || redirectEvent === "decline") {
                const homeAddendumAlert = alerts.find(a => a.data?.FormId === "HomeAddendum");
                const updateAction = redirectEvent === "signing_complete" ? "Complete" : "Remove";
                if (homeAddendumAlert) {
                    updateAlert(
                        { alertId: homeAddendumAlert.id, action: updateAction, stateToken: returnedState },
                        () => {
                            getAlerts();
                            if (redirectEvent === "signing_complete") {
                                confirmationToastRef.current.show();
                            }
                        }
                    );
                }
            } else {
                infoToastRef.current.show();
            }
        }
    }, [alerts, searchParams]);

    function getAlerts() {
        if (clientConfiguration?.enableAlerts) {
            getAgencyAlerts(response => {
                setAlerts(response || []);
                setLoading(false);
            });
        } else {
            setAlerts([]);
            setLoading(false);
        }
    }

    useEffect(() => {
        let redirectEvent = searchParams.get("event");
        let isSweepDocusign = searchParams.get("isSweepDocusign");
        let isBranchDocusign = searchParams.get("isBranchDocusign");
        let isHomeDocusign = searchParams.get("isHomeDocusign");
        let branchAgencyId = searchParams.get("branchAgencyId");
        let sweepEmail = searchParams.get("sweepEmail");

        if (!redirectEvent) {
            return;
        } else {
            let signingComplete = !redirectErrorEvents.includes(redirectEvent) ? "true" : "false";

            getAgencyProfile(user.agencyId, response => {
                if (isSweepDocusign) {
                    navigate("/email-settings", { state: { agencyProfile: response, branchProfile: null, isBranch: false, isDocusignCompleted: signingComplete, redirectFromDocusign: true, sweepEmail: sweepEmail } });
                }
                else if (isBranchDocusign) {
                    if (branchAgencyId) {
                        getAgencyProfile(branchAgencyId, branchResponse => {
                            navigate("/email-settings", { state: { agencyProfile: response, branchProfile: branchResponse, isBranch: true, isDocusignCompleted: signingComplete, redirectFromDocusign: true, sweepEmail: sweepEmail } });
                        });
                    }
                } else if (isHomeDocusign) {
                    recompileProfile();
                } else {
                    let updatedAgencyProfile = {
                        ...response,
                        taxInfoOnFile: signingComplete === "true" ? true : false
                    }

                    updateTaxInfoOnFile(updatedAgencyProfile,
                        () => {
                            if (signingComplete === "true") {
                                confirmationToastRef.current.show();
                            } else {
                                infoToastRef.current.show();
                            }
                        }
                    );
                }
            });
        }
    }, []);

    if (Object.entries(show).every(([key, value]) => !value)) {
        return <Error />;
    }

    if (loading) {
        return <Loader type={Types.Section} />;
    }

    return <Layout alerts={alerts}>
        <Toast
            toastRef={confirmationToastRef}
            header="Success"
            description="Your document has been signed successfully."
            toastType={ToastTypes.Confirmation}
        />

        <Toast
            toastRef={infoToastRef}
            description="We have not received your signed document. Please try again."
            toastType={ToastTypes.Info}
        />

        <PageHeader
            header="Agency Management System"
            showBottomBorder={false}
            numberOfAlerts={alerts?.length}
        >
            <p>All the tools you'll need to manage Independent Agency profiles, set agency defaults, and more.</p>
        </PageHeader>
        <div className='container margin-top' >
            <Tabs content={tabContent} initialTabOpen={openTab} />
        </div>
    </Layout>;
}

const redirectErrorEvents = ["cancel", "decline", "exception", "session_timeout", "ttl_expired"];
Home.propTypes = {
    tabName: PropTypes.string,
};
